import { useState } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function HeroSection() {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,nofollow');
  };

  return (
    <section className="relative w-full min-h-[420px] md:min-h-[600px] flex items-center overflow-hidden bg-background-50">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-100/60 via-background-50/80 to-background-50"></div>
        <div
          className="absolute -right-20 md:-right-10 top-1/2 -translate-y-1/2 w-[320px] md:w-[500px] h-[400px] md:h-[580px] rounded-3xl overflow-hidden"
          style={{ opacity: 0.25 }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20fashion%20flat%20lay%20with%20cream%20sneakers%2C%20folded%20beige%20hoodie%2C%20gold%20watch%2C%20and%20leather%20accessories%20on%20warm%20off%20white%20linen%20texture%20background%2C%20soft%20window%20light%20from%20left%2C%20editorial%20minimalist%20product%20photography%2C%20peaceful%20aesthetic%2C%20harmonious%20neutral%20composition&width=1000&height=1160&seq=hero-bg-decor-v3&orientation=portrait"
            alt="Sugargoo fashion collection"
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="relative w-full px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16">
            <div className="w-full lg:w-[42%] pt-16 md:pt-24 pb-10 md:pb-20">
              <span className="inline-block text-xs md:text-sm text-primary-500 font-medium tracking-wide uppercase mb-4">
                2026 Updated
              </span>
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 leading-[1.15] mb-6">
                Sugargoo<br />Spreadsheet
              </h1>
              <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8 max-w-md">
                Best Finds &amp; Trusted Links — Your curated directory of verified Sugargoo products, real QC photos, and community-vetted sellers.
              </p>

              <div className="relative mb-5">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="w-full flex items-center justify-between px-5 py-3.5 bg-background-50 border-2 border-background-200 rounded-xl text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer"
                >
                  <span>{selectedCategory || 'Select a category to browse...'}</span>
                  <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-background-50 border border-background-200/70 rounded-xl shadow-lg z-20 max-h-72 overflow-y-auto">
                    {categories.map((cat) => (
                      <button
                        key={cat.name}
                        onClick={() => handleCategorySelect(cat)}
                        className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 border-2 border-foreground-300 text-foreground-800 rounded-full text-sm font-medium hover:bg-foreground-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Sugargoo Spreadsheet
                  <i className="ri-arrow-right-up-line"></i>
                </a>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Buy It Now
                  <i className="ri-arrow-right-up-line"></i>
                </a>
              </div>

              <p className="mt-4 text-xs text-foreground-400 leading-relaxed max-w-sm">
                All external links open in a new tab. We curate and organize the best Sugargoo spreadsheet resources — updated daily for the community.
              </p>
            </div>

            <div className="w-full lg:w-[58%] flex flex-col md:flex-row gap-4 md:gap-6 pb-10 md:pb-20">
              <div className="w-full md:w-[55%] rounded-2xl overflow-hidden h-[280px] md:h-[440px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20streetwear%20editorial%20featuring%20cream%20chunky%20sneakers%2C%20oversized%20taupe%20hoodie%2C%20and%20dark%20denim%20jeans%20on%20warm%20sand%20colored%20minimalist%20backdrop%2C%20soft%20diffused%20golden%20light%2C%20clean%20modern%20styling%2C%20premium%20textile%20textures%2C%20elevated%20fashion%20photography&width=900&height=1200&seq=hero-main-fashion-v3&orientation=portrait"
                  alt="Sugargoo fashion finds"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex md:flex-col gap-4 md:gap-4 w-full md:w-[45%]">
                <p className="text-xs text-foreground-500 uppercase tracking-wide flex items-center gap-2">
                  Quick Categories
                  <i className="ri-arrow-right-up-line"></i>
                </p>
                <div className="flex md:flex-col gap-3 w-full">
                  <div className="flex-1 rounded-xl overflow-hidden h-[130px] md:h-[210px]">
                    <img
                      src="https://readdy.ai/api/search-image?query=Closeup%20of%20pristine%20white%20leather%20low%20top%20sneakers%20with%20cream%20accents%20on%20warm%20sand%20surface%2C%20natural%20window%20light%20creating%20gentle%20shadows%2C%20editorial%20sneaker%20photography%2C%20clean%20minimal%20composition%2C%20texture%20details%20sharp%2C%20lifestyle%20aesthetic&width=800&height=800&seq=hero-sneakers-v3&orientation=squarish"
                      alt="Sneakers"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex gap-3 flex-1">
                    <div className="flex-1 rounded-xl overflow-hidden h-[130px] md:h-[200px]">
                      <img
                        src="https://readdy.ai/api/search-image?query=Premium%20heathered%20grey%20cotton%20hoodie%20neatly%20folded%20on%20warm%20cream%20textured%20surface%2C%20soft%20morning%20light%20through%20sheer%20curtain%2C%20editorial%20minimal%20fashion%20photography%2C%20fabric%20texture%20visible%2C%20clean%20simple%20styling%2C%20cozy%20elegant%20vibe&width=600&height=800&seq=hero-hoodie-v3&orientation=portrait"
                        alt="Hoodie"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="flex-1 rounded-xl overflow-hidden h-[130px] md:h-[200px]">
                      <img
                        src="https://readdy.ai/api/search-image?query=Olive%20green%20lightweight%20bomber%20jacket%20draped%20elegantly%20on%20warm%20cream%20minimalist%20backdrop%2C%20soft%20afternoon%20natural%20light%2C%20editorial%20outerwear%20photography%2C%20fabric%20texture%20and%20stitching%20details%20visible%2C%20clean%20modern%20composition%2C%20sophisticated%20casual%20style&width=600&height=800&seq=hero-jacket-v3&orientation=portrait"
                        alt="Jacket"
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}